import React, { useState, useEffect, useCallback } from "react";
import styles from "./PriceRangeSlider.module.css";

interface PriceRangeSliderProps {
  min: number;
  max: number;
  values: number[];
  onChange: (values: number[]) => void;
  className?: string;
  currencySymbol?: string;
  debounceTime?: number;
}

const PriceRangeSlider: React.FC<PriceRangeSliderProps> = ({
  min,
  max,
  values,
  onChange,
  className = "",
  currencySymbol = "₹",
  debounceTime = 500,
}) => {
  const [localValues, setLocalValues] = useState(values);
  const [isDragging, setIsDragging] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  // Sync with parent values
  useEffect(() => {
    if (!isDragging) {
      setLocalValues(values);
    }
  }, [values, isDragging]);

  // Debounce the onChange callback
  const debouncedOnChange = useCallback(
    (newValues: number[]) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        onChange(newValues);
        setIsDragging(false);
      }, debounceTime);

      setDebounceTimer(timer);
    },
    [debounceTime, onChange, debounceTimer]
  );

  const handleValueChange = (newValues: number[]) => {
    setLocalValues(newValues);
    setIsDragging(true);
    debouncedOnChange(newValues);
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = parseInt(e.target.value);
    if (newMin <= localValues[1]) {
      handleValueChange([newMin, localValues[1]]);
    }
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = parseInt(e.target.value);
    if (newMax >= localValues[0]) {
      handleValueChange([localValues[0], newMax]);
    }
  };

  // Calculate the fill gradient positions
  const minPos = ((localValues[0] - min) / (max - min)) * 100;
  const maxPos = ((localValues[1] - min) / (max - min)) * 100;

  return (
    <div className={`${styles.rangeSlider} ${className}`}>
      <div className={styles.valueDisplay}>
        <div className={styles.valueContainer}>
          <label className={styles.valueLabel}>Min:</label>
          <span className={styles.value}>
            {currencySymbol}
            {localValues[0].toLocaleString()}
          </span>
        </div>
        <div className={styles.valueContainer}>
          <label className={styles.valueLabel}>Max:</label>
          <span className={styles.value}>
            {currencySymbol}
            {localValues[1].toLocaleString()}
          </span>
        </div>
      </div>

      <div className={styles.sliderContainer}>
        <div className={styles.sliderTrack}>
          <div
            className={styles.sliderRange}
            style={{
              left: `${minPos}%`,
              right: `${100 - maxPos}%`,
            }}
          />
        </div>

        <input
          type="range"
          min={min}
          max={max}
          value={localValues[0]}
          onChange={handleMinChange}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
          className={`${styles.slider} ${styles.sliderMin}`}
          aria-label="Minimum price"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={localValues[1]}
          onChange={handleMaxChange}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
          className={`${styles.slider} ${styles.sliderMax}`}
          aria-label="Maximum price"
        />

        <div className={styles.sliderThumb} style={{ left: `${minPos}%` }}>
          <div className={styles.thumbTooltip}>
            {currencySymbol}
            {localValues[0]}
          </div>
        </div>
        <div className={styles.sliderThumb} style={{ left: `${maxPos}%` }}>
          <div className={styles.thumbTooltip}>
            {currencySymbol}
            {localValues[1]}
          </div>
        </div>
      </div>

      {isDragging && (
        <div className={styles.draggingIndicator}>Release to apply filters</div>
      )}
    </div>
  );
};

export default PriceRangeSlider;
